import { Button } from "@/components/atoms/button/Button";
import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { ButtonSizes, ButtonVariants } from "@/types/enums";
import { renderMedia } from "@/utils/renderMediaComponent";
import { Box, Flex, Grid, GridItem, Heading } from "@chakra-ui/react";
import DOMPurify from "isomorphic-dompurify";

export interface CallToActionProps {
  title: string;
  primaryButtonText?: string;
  primaryButtonUrl?: string;
  mediaData?: MediaImageData;
}

export const CallToAction = ({
  title,
  primaryButtonText,
  primaryButtonUrl,
  mediaData,
}: CallToActionProps) => {
  const hasImage = !!mediaData;
  return (
    <Box
      width="100%"
      pb={{ base: "1.5rem", md: "2.5rem" }}
      pt={
        hasImage
          ? { base: "0.75rem", md: "2.5rem" }
          : { base: "1.5rem", md: "2.5rem" }
      }
      px={{ base: "0.75rem", md: "2.5rem" }}
      backgroundColor="brand.grey"
      borderRadius=".5rem"
    >
      <Grid margin="auto" templateColumns="repeat(12, 1fr)">
        {hasImage ? (
          <>
            {/* Image column - hidden on mobile, shown on desktop */}
            <GridItem
              colSpan={{ base: 12, md: 5 }}
              display={{ base: "block", md: "block" }}
              order={{ base: 1, md: 1 }}
              px={{ base: 0, md: 0 }}
            >
              {renderMedia(mediaData, { base: "cta" }, { base: 0.7 })}
            </GridItem>
            {/* Content column */}
            <GridItem
              colSpan={{ base: 12, md: 7 }}
              pl={{ base: 0, md: "2.5rem" }}
              display="flex"
              alignItems="center"
              order={{ base: 2, md: 2 }}
            >
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                mt={{ base: "0.75rem", md: 0 }}
              >
                <Heading
                  as={"h4"}
                  fontSize={{ base: "2xl", md: "2rem", lg: "5xl" }}
                  textAlign="left"
                  fontWeight={400}
                  color={"brand.aubergine"}
                  sx={{
                    strong: {
                      fontWeight: 400,
                      color: "brand.cyan",
                      fontFamily: "'Lausanne', sans-serif",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      title.replace(/<\/p>/g, "</br>"),
                      {
                        ALLOWED_TAGS: ["strong", "br"],
                      }
                    ),
                  }}
                />

                <Box mt="1rem">
                  {primaryButtonText && primaryButtonUrl && (
                    <Button
                      text={primaryButtonText}
                      url={primaryButtonUrl}
                      buttonVariant={ButtonVariants.Primary}
                      buttonSize={ButtonSizes.Small}
                      ariaLabel={primaryButtonText}
                    />
                  )}
                </Box>
              </Flex>
            </GridItem>
          </>
        ) : (
          // Layout without image
          <GridItem colSpan={12} px={{ base: 0, md: 0 }}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              width="100%"
            >
              <Box flex="1">
                <Heading
                  as={"h4"}
                  fontSize={{ base: "2xl", md: "2rem", lg: "5xl" }}
                  textAlign="left"
                  fontWeight={400}
                  color={"brand.aubergine"}
                  sx={{
                    strong: {
                      fontWeight: 400,
                      color: "brand.cyan",
                      fontFamily: "'Lausanne', sans-serif",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      title.replace(/<\/p>/g, "</br>"),
                      {
                        ALLOWED_TAGS: ["strong", "br"],
                      }
                    ),
                  }}
                />
              </Box>
              <Flex
                gap={4}
                mt={{ base: "1rem", md: 0 }}
                ml={{ base: 0, md: 8 }}
                direction={{ base: "column", sm: "row" }}
                align={{ base: "flex-start", md: "center" }}
                flexShrink={0}
              >
                {primaryButtonText && primaryButtonUrl && (
                  <Button
                    text={primaryButtonText}
                    url={primaryButtonUrl}
                    buttonVariant={ButtonVariants.Primary}
                    buttonSize={ButtonSizes.Small}
                    ariaLabel={primaryButtonText}
                  />
                )}
              </Flex>
            </Flex>
          </GridItem>
        )}
      </Grid>
    </Box>
  );
};
